import { create } from 'zustand';
import type { Avatar } from '@sokdaq/protocol';

/**
 * 사용자 설정.
 *
 * 채팅 상태나 창 상태와 섞지 않는다. 이 값들은 사람이 고른 취향이고,
 * 서버와 무관하며, 기기마다 다를 수 있다.
 */

const STORAGE_KEY = 'sokdaq.settings';

/**
 * 알림 범위.
 *
 * '속닥만'이 있는 이유: 사무실에서 종일 켜두면 공개 대화는 흘려보내고
 * 나를 향한 속닥만 챙기고 싶어진다.
 */
export type NotifyMode = 'off' | 'all' | 'whisper';

export interface Settings {
  notify: NotifyMode;
  sound: boolean;
  /** 직접 고른 아바타. 없으면 닉네임에서 자동으로 뽑는다. */
  avatar: Avatar | null;
}

export interface SettingsState extends Settings {
  setNotify: (mode: NotifyMode) => void;
  setSound: (on: boolean) => void;
  setAvatar: (avatar: Avatar | null) => void;
}

const DEFAULTS: Settings = { notify: 'all', sound: true, avatar: null };

function load(): Settings {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw === null) return DEFAULTS;

    const parsed = JSON.parse(raw) as Partial<Settings>;
    return {
      notify:
        parsed.notify === 'off' || parsed.notify === 'whisper' || parsed.notify === 'all'
          ? parsed.notify
          : DEFAULTS.notify,
      sound: typeof parsed.sound === 'boolean' ? parsed.sound : DEFAULTS.sound,
      avatar: parsed.avatar ?? null,
    };
  } catch {
    // 저장값이 깨졌으면 기본값으로 시작한다. 다음 저장 때 덮어써진다.
    return DEFAULTS;
  }
}

function persist(settings: Settings): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  } catch {
    /* 저장 못 해도 이번 세션은 그대로 쓴다. */
  }
}

export const useSettingsStore = create<SettingsState>((set, get) => ({
  ...load(),

  setNotify: (notify) => {
    set({ notify });
    persist({ ...get(), notify });
  },
  setSound: (sound) => {
    set({ sound });
    persist({ ...get(), sound });
  },
  setAvatar: (avatar) => {
    set({ avatar });
    persist({ ...get(), avatar });
  },
}));

/** 이 메시지에 알림을 낼지. 위장 여부는 호출부가 따로 판단한다. */
export function shouldNotify(mode: NotifyMode, kind: string): boolean {
  if (mode === 'off') return false;
  if (mode === 'whisper') return kind === 'whisper';
  return true;
}
